import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { UseMutationResult } from '@tanstack/react-query';
import GoogleLogoIcon from '../icons/GoogleLogoIcon';
import ConfirmDialog from '../common/ConfirmDialog';
import GradientCard from '../gamification/GradientCard';
import { EASE_OUT } from '@/lib/motion';
import type { AuthorizeResponse, Connection } from '../../types/integrations';

type StartConnectMutation = UseMutationResult<
  { status: 'success'; data: AuthorizeResponse },
  unknown,
  void,
  unknown
>;

type DisconnectMutation = UseMutationResult<
  { status: 'success'; data: { id: string } },
  unknown,
  string,
  unknown
>;

interface IntegrationCardProps {
  provider: 'google';
  connection: Connection | null;
  startConnect: StartConnectMutation;
  disconnect: DisconnectMutation;
}

const PROVIDER_META = {
  google: {
    name: 'Google Calendar',
    description:
      'Sync course events to your Tereiko calendar and invite learners straight from Google.',
    Icon: GoogleLogoIcon,
  },
} as const;

const formatLastSync = (iso: string | null): string | null => {
  if (!iso) return null;
  try {
    return `Last synced ${format(parseISO(iso), 'PP p')}`;
  } catch {
    return null;
  }
};

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  provider,
  connection,
  startConnect,
  disconnect,
}) => {
  const meta = PROVIDER_META[provider];
  const Icon = meta.Icon;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isActive = connection?.status === 'active';
  const needsReauth =
    !!connection &&
    (connection.status === 'reauth_required' ||
      connection.status === 'expired' ||
      connection.status === 'revoked');

  const handleConnect = () => startConnect.mutate();
  const handleDisconnectConfirmed = () => {
    if (!connection) return;
    setConfirmOpen(false);
    disconnect.mutate(connection.id);
  };

  return (
    <GradientCard className="rounded-lg">
      <div className="p-6 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2 border border-border-primary/20 flex items-center justify-center bg-surface-primary shrink-0">
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-text-primary">{meta.name}</h3>
            <p className="text-sm text-text-secondary mt-0.5">{meta.description}</p>
          </div>
        </div>

        {/* Reauth banner */}
        {needsReauth && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
            className="flex items-start gap-3 p-4 rounded-2 bg-warning-500/10 dark:bg-warning-500/16 border border-warning-500/30"
          >
            <ExclamationTriangleIcon className="w-5 h-5 text-warning-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">Reconnect Needed</p>
              <p className="text-sm text-text-secondary mt-0.5">
                Your Google access has expired. Reconnect to keep events syncing.
              </p>
            </div>
            <button
              type="button"
              onClick={handleConnect}
              disabled={startConnect.isPending}
              className="btn btn-primary btn-sm shrink-0 active:scale-[0.97] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {startConnect.isPending ? 'Opening…' : 'Reconnect'}
            </button>
          </motion.div>
        )}

        {/* Status row */}
        <div className="flex items-center justify-between gap-4 pt-2 border-t border-border-primary/15">
          {isActive && connection ? (
            <>
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">
                  Connected as {connection.account_email ?? 'unknown account'}
                </p>
                {formatLastSync(connection.last_synced_at) && (
                  <p className="text-xs text-text-tertiary mt-0.5">
                    {formatLastSync(connection.last_synced_at)}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={disconnect.isPending}
                className="btn btn-outline btn-sm shrink-0 active:scale-[0.97] transition-transform disabled:opacity-50"
              >
                {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </>
          ) : !needsReauth ? (
            <>
              <p className="text-sm text-text-secondary">Not connected</p>
              <button
                type="button"
                onClick={handleConnect}
                disabled={startConnect.isPending}
                className="btn btn-primary btn-sm shrink-0 active:scale-[0.97] transition-transform disabled:opacity-50"
              >
                {startConnect.isPending ? 'Opening…' : 'Connect'}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {confirmOpen && (
        <ConfirmDialog
          title="Disconnect Google Calendar?"
          message="Tereiko will stop syncing events to this account. Your existing Google events will not be deleted."
          confirmLabel="Disconnect"
          confirmVariant="danger"
          onConfirm={handleDisconnectConfirmed}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </GradientCard>
  );
};

export default IntegrationCard;
